import { useEffect, useMemo, useRef, useState } from 'react';
import { IonButton } from '@ionic/react';
import { Bell, BellOff, ChevronDown, MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../ui/Avatar';
import { ConnectionTypeModal } from './ConnectionTypeModal';
import { LeaveGroupModal } from './LeaveGroupModal';
import { useDeleteConnection, useMyConnections } from '../../hooks/useConnections';
import {
  useSnoozeChef,
  useSnoozedChefs,
  useUnsnoozeChef,
} from '../../hooks/useNotificationMutes';
import { useProfileStore } from '../../store/useProfileStore';
import { effectiveConnectionType, isFromQrCode, type Connection } from '../../types/connection';

/**
 * A single accepted-connection row. The initiator can re-categorize the type
 * via a chooser modal opened from the current-type pill; the recipient sees a
 * static label (re-categorize is initiator-only).
 */
const ConnectionRow: React.FC<{ connection: Connection; myId?: string }> = ({
  connection: c,
  myId,
}) => {
  const { t } = useTranslation('connections');
  const remove = useDeleteConnection();
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const groupMenuRef = useRef<HTMLDivElement>(null);

  // Close the popover when the user taps anywhere outside it (or hits Escape).
  // The listener attaches only while open so idle rows don't pay for it.
  useEffect(() => {
    if (!groupMenuOpen) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!groupMenuRef.current) return;
      if (!groupMenuRef.current.contains(e.target as Node)) {
        setGroupMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setGroupMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [groupMenuOpen]);

  // Only the initiator can re-categorize. The recipient sees a static label.
  // Campaign-driven rows (auto-created by a marketing QR fan-out) also lock
  // the type — provenance belongs to the campaign, not the initiator — so
  // treat them like the recipient view even when the caller is the initiator.
  const isInitiator = c.profileId === myId;
  const isCampaign = isFromQrCode(c);
  const canRetype = isInitiator && !isCampaign;
  const other = isInitiator ? c.connectedProfile : c.profile;
  const name = other?.displayName || other?.email || t('list.someone');

  // Per-chef new-dish snooze for this connection. The snoozed set is shared
  // across all rows (one deduped ['notification-mutes'] query).
  const otherId = other?.id;
  const { data: snoozedChefs } = useSnoozedChefs();
  const snoozeChef = useSnoozeChef();
  const unsnoozeChef = useUnsnoozeChef();
  const isSnoozed =
    !!otherId && (snoozedChefs ?? []).some((m) => m.mutedProfileId === otherId);
  const snoozePending = snoozeChef.isPending || unsnoozeChef.isPending;
  const toggleSnooze = () => {
    if (!otherId) return;
    if (isSnoozed) unsnoozeChef.mutate(otherId);
    else snoozeChef.mutate(otherId);
  };
  // What we surface: share_* provenance collapses to "connection". The raw
  // c.type stays on the row untouched.
  const effective = effectiveConnectionType(c.type);

  // Row wraps as a whole. The identity keeps a text-relative floor (min-w in
  // rem, which grows with the OS/browser text size) so that as text scales up
  // the content+Remove stop fitting and Remove drops to its own line instead
  // of squishing the name.
  return (
    <li className="flex flex-wrap items-center gap-3 py-3">
      <div className="flex min-w-[10rem] flex-1 items-center gap-3">
        <Avatar src={other?.avatarUrl} name={name} size="md" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{name}</p>
          {canRetype ? (
            <button
              type="button"
              onClick={() => setTypeModalOpen(true)}
              aria-label={t('type.changeAria')}
              className="mt-1 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {t(`type.${effective}`)}
              <ChevronDown className="h-3 w-3" />
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">{t(`type.${effective}`)}</p>
          )}
        </div>
      </div>
      {otherId && (
        <button
          type="button"
          onClick={toggleSnooze}
          disabled={snoozePending}
          aria-label={isSnoozed ? t('snooze.unsnooze') : t('snooze.snooze')}
          title={isSnoozed ? t('snooze.unsnooze') : t('snooze.snooze')}
          className={`ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition hover:bg-black/5 active:scale-95 disabled:opacity-50 ${
            isSnoozed ? 'text-muted-foreground' : 'text-brand-primary'
          }`}
        >
          {isSnoozed ? <BellOff className="h-[18px] w-[18px]" /> : <Bell className="h-[18px] w-[18px]" />}
        </button>
      )}
      <IonButton
        size="small"
        fill="outline"
        color="medium"
        className="shrink-0"
        onClick={() => remove.mutate(c.id)}
        disabled={remove.isPending}
      >
        {t('list.remove')}
      </IonButton>
      {c.qrCode?.connectionGroup && (
        <div className="flex w-full items-center gap-2">
          <p className="flex-1 text-xs italic text-muted-foreground line-clamp-1">
            {t('list.connectedThrough')}
            {c.qrCode.connectionGroup}
          </p>
          {/* 3-dot menu — the only entry point today is "Leave this group?".
              Wrapped in a positioning ref so click-outside can find the
              popover as a descendant of the trigger's wrapper. */}
          <div className="relative" ref={groupMenuRef}>
            <button
              type="button"
              onClick={() => setGroupMenuOpen((v) => !v)}
              aria-label={t('list.groupMenu')}
              aria-haspopup="menu"
              aria-expanded={groupMenuOpen}
              className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-black/5 active:scale-95 transition"
            >
              <MoreVertical className="h-3 w-3" />
            </button>
            {groupMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 top-full z-20 mt-1 min-w-[10rem] rounded-lg border border-border bg-card shadow-lg"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setGroupMenuOpen(false);
                    setLeaveModalOpen(true);
                  }}
                  className="block w-full px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
                >
                  {t('list.leaveGroupItem')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {canRetype && (
        <ConnectionTypeModal
          isOpen={typeModalOpen}
          onClose={() => setTypeModalOpen(false)}
          connection={c}
        />
      )}
      {c.qrCode?.connectionGroup && (
        <LeaveGroupModal
          isOpen={leaveModalOpen}
          onClose={() => setLeaveModalOpen(false)}
          connectionId={c.id}
          groupName={c.qrCode.connectionGroup}
        />
      )}
    </li>
  );
};

/**
 * The caller's accepted connections. For each row the "other party" is
 * whichever side isn't the caller: when the caller is the initiator
 * (`profileId === me`) it's `connectedProfile`, otherwise it's `profile`.
 * Both sides are preloaded by the list endpoint, so no per-row fetch.
 */
export const ConnectionsList: React.FC = () => {
  const { t } = useTranslation('connections');
  const myId = useProfileStore((s) => s.activeProfile?.id);
  const { data: connections, isLoading } = useMyConnections('accepted');

  // Non-group connections first, group connections last. Stable within
  // each partition — the BE already returns rows in the order it wants,
  // so we just push campaign rows to the bottom without disturbing the
  // relative order of everything else.
  const sorted = useMemo(() => {
    if (!connections) return connections;
    return [...connections].sort((a, b) => {
      const ag = isFromQrCode(a) ? 1 : 0;
      const bg = isFromQrCode(b) ? 1 : 0;
      return ag - bg;
    });
  }, [connections]);

  if (isLoading) return null;
  if (!sorted || sorted.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('list.empty')}</p>;
  }

  return (
    <ul className="divide-y divide-border">
      {sorted.map((c) => (
        <ConnectionRow key={c.id} connection={c} myId={myId} />
      ))}
    </ul>
  );
};
